import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';

/**
 * ContactUs - Contact information and ticket management system
 * 
 * Features:
 * - File attachment support for tickets (images, PDFs, documents)
 * - Ticket log visible to admins (with tickets.admin permission) - they see all tickets
 * - Regular users can see their own submitted tickets
 * - Anyone can filter the list: Open (not closed), Closed, or All
 * - Admins can respond to tickets inline
 * - Email notifications sent to admins on new tickets and to users on responses
 * - Confirmation message after ticket submission
 */
const ContactUs = () => {
  const { instance, accounts } = useMsal();
  const currentUser = accounts[0];
  const fileInputRef = useRef(null);
  
  // State
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isTicketAdmin, setIsTicketAdmin] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const { toast, showToast } = useToast();
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  const [ticketReplies, setTicketReplies] = useState({});
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [responseStatus, setResponseStatus] = useState('');
  const [, setRespondingLoading] = useState(false);
  const [newTicket, setNewTicket] = useState({
    issue_category: '',
    issue: ''
  });
  const [statusFilter, setStatusFilter] = useState('open'); // open | closed | all

  const isClosedStatus = (status) => (status || '').toLowerCase() === 'closed';
  const openCount = tickets.filter(t => !isClosedStatus(t.status)).length;
  const closedCount = tickets.filter(t => isClosedStatus(t.status)).length;
  const filteredTickets = tickets.filter(t => {
    if (statusFilter === 'open') return !isClosedStatus(t.status);
    if (statusFilter === 'closed') return isClosedStatus(t.status);
    return true;
  });


  const issueCategories = [
    'Bug Report',
    'Feature Request',
    'Access Issue',
    'Data Problem',
    'Performance Issue',
    'Training Request',
    'General Question',
    'Other'
  ];

  // Fetch user permissions to determine if they have tickets.admin permission
  const fetchUserPermissions = useCallback(async () => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/auth/me'
      );
      
      const user = response.data?.user;
      if (!user) return;

      // Superadmin always has ticket admin access
      if (user.primary_role_id === 'superadmin' || user.effective_authority_level === 'System' || user.role_authority_level === 'System') {
        setIsTicketAdmin(true);
        return;
      }

      // V3: authority_permissions is a string array like ["*", "tickets.admin"]
      const authPerms = user.authority_permissions;
      if (Array.isArray(authPerms) && (authPerms.includes('*') || authPerms.includes('tickets.admin'))) {
        setIsTicketAdmin(true);
        return;
      }

      // V1 fallback: permissions as array of objects with permission_name
      const perms = user.permissions;
      if (Array.isArray(perms)) {
        const hasAdmin = perms.some(p => typeof p === 'string' ? (p === '*' || p === 'tickets.admin') : p?.permission_name === 'tickets.admin');
        setIsTicketAdmin(hasAdmin);
        return;
      }

      setIsTicketAdmin(false);
    } catch (err) {
      console.error('Error fetching user permissions:', err);
    }
  }, [instance, accounts]);

  // Fetch tickets (only superadmin will see them)
  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/tickets'
      );
      
      if (response.data.success) {
        setTickets(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchUserPermissions();
    fetchTickets();
  }, [fetchUserPermissions, fetchTickets]);

  // Handle responding to a ticket (admin only) — wired up in TicketReplies modal
  // eslint-disable-next-line no-unused-vars
  const handleRespondToTicket = async (ticketId) => {
    if (!responseText.trim() && !responseStatus) {
      showToast('Please enter a response or select a status', 'error');
      return;
    }

    try {
      setRespondingLoading(true);
      const payload = {};
      if (responseText.trim()) payload.response = responseText.trim();
      if (responseStatus) payload.status = responseStatus;

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/tickets/${ticketId}`,
        payload
      );

      if (response.data.success) {
        showToast('Response sent successfully! User will be notified via email.', 'success');
        setRespondingTo(null);
        setResponseText('');
        setResponseStatus('');
        fetchTickets();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to send response', 'error');
    } finally {
      setRespondingLoading(false);
    }
  };

  // Handle deleting a ticket (admin only)
  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket? This cannot be undone.')) {
      return;
    }

    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/tickets/${ticketId}`
      );

      if (response.data.success) {
        showToast('Ticket deleted successfully', 'success');
        fetchTickets();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete ticket', 'error');
    }
  };

  // Fetch replies for a ticket
  const fetchReplies = async (ticketId) => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/tickets/${ticketId}/replies`);
      if (response.data.success) {
        setTicketReplies(prev => ({ ...prev, [ticketId]: response.data.data || [] }));
      }
    } catch (err) {
      console.error('Error fetching replies:', err);
    }
  };

  // Send a reply (user or admin)
  const handleSendReply = async (ticketId) => {
    if (!replyText.trim()) return;
    setReplyLoading(true);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', `/tickets/${ticketId}/replies`, { message: replyText.trim() });
      if (response.data.success) {
        showToast('Reply sent', 'success');
        setReplyText('');
        fetchReplies(ticketId);
        fetchTickets();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to send reply', 'error');
    } finally {
      setReplyLoading(false);
    }
  };

  // Close a ticket (admin only)
  const handleCloseTicket = async (ticketId) => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'PUT', `/tickets/${ticketId}/close`);
      if (response.data.success) {
        showToast('Ticket closed', 'success');
        fetchTickets();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to close ticket', 'error');
    }
  };

  // Reopen a ticket (admin only)
  const handleReopenTicket = async (ticketId) => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'PUT', `/tickets/${ticketId}/reopen`);
      if (response.data.success) {
        showToast('Ticket reopened', 'success');
        fetchTickets();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to reopen ticket', 'error');
    }
  };

  // Handle downloading ticket attachment (authenticated)
  const handleDownloadAttachment = async (ticketId, attachmentName) => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/tickets/${ticketId}/attachment`,
        null,
        { responseType: 'blob' }
      );
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachmentName || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      showToast('Failed to download attachment', 'error');
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        showToast('File size must be less than 10MB', 'error');
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
    }
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Submit new ticket with optional attachment
  const handleSubmitTicket = async () => {
    if (!newTicket.issue_category || !newTicket.issue) {
      showToast('Please select a category and describe your issue', 'error');
      return;
    }

    try {
      setSubmitting(true);
      
      // Use FormData to support file upload
      const formData = new FormData();
      formData.append('issue_category', newTicket.issue_category);
      formData.append('issue', newTicket.issue);
      if (selectedFile) {
        formData.append('attachment', selectedFile);
      }
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/tickets',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.success) {
        // Show confirmation message instead of just a toast
        setConfirmationMessage(response.data.message || 'Thank you for your feedback! Your contribution helps make this platform better for everyone.');
        setShowConfirmation(true);
        setShowNewTicketForm(false);
        setNewTicket({ issue_category: '', issue: '' });
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Refresh tickets
        fetchTickets();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to submit ticket', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const getStatusStyle = (s) => {
      switch (s?.toLowerCase()) {
        case 'open':
          return { bg: '#ffc107', color: '#212529' };
        case 'in progress':
          return { bg: '#17a2b8', color: 'white' };
        case 'resolved':
          return { bg: '#28a745', color: 'white' };
        case 'closed':
          return { bg: '#6c757d', color: 'white' };
        default:
          return { bg: '#f8f9fa', color: '#212529' };
      }
    };
    
    const style = getStatusStyle(status);
    return (
      <span style={{
        backgroundColor: style.bg,
        color: style.color,
        padding: '0.25rem 0.75rem',
        borderRadius: '12px',
        fontSize: '0.8rem',
        fontWeight: '600'
      }}>
        {status || 'Open'}
      </span>
    );
  };

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Toast */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '1rem 1.5rem',
          backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545',
          color: 'white',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          animation: 'slideIn 0.3s ease'
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 style={{ margin: 0, color: '#333', fontSize: '2rem' }}>
          📞 Contact Us & Support
        </h1>
        <p style={{ color: '#666', fontSize: '1.1rem', marginTop: '0.5rem' }}>
          Have a question or need assistance? We're here to help!
        </p>
      </div>

      {/* Ticket System */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <h2 style={{ margin: 0, color: '#333', fontSize: '1.3rem' }}>
              🎫 Support Tickets
            </h2>
            <p style={{ color: '#666', margin: '0.25rem 0 0', fontSize: '0.9rem' }}>
              Submit a ticket and we'll get back to you as soon as possible
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div
              role="tablist"
              aria-label="Filter tickets by status"
              style={{ display: 'inline-flex', border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden', backgroundColor: '#fff' }}
            >
              {[
                { id: 'open', label: 'Open', count: openCount },
                { id: 'closed', label: 'Closed', count: closedCount },
                { id: 'all', label: 'All', count: tickets.length }
              ].map((opt, i) => {
                const active = statusFilter === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setStatusFilter(opt.id)}
                    style={{
                      padding: '0.5rem 0.9rem',
                      border: 'none',
                      borderLeft: i === 0 ? 'none' : '1px solid #dee2e6',
                      backgroundColor: active ? (opt.id === 'closed' ? '#6c757d' : opt.id === 'open' ? '#0d6efd' : '#343a40') : '#fff',
                      color: active ? '#fff' : '#495057',
                      fontSize: '0.85rem',
                      fontWeight: active ? 700 : 500,
                      cursor: 'pointer'
                    }}
                  >
                    {opt.label} ({opt.count})
                  </button>
                );
              })}
            </div>
          
            <button
              onClick={() => setShowNewTicketForm(!showNewTicketForm)}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: showNewTicketForm ? '#6c757d' : '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {showNewTicketForm ? '✕ Cancel' : '➕ New Ticket'}
            </button>
          </div>
        </div>

        {/* New Ticket Form */}
        {showNewTicketForm && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{ margin: '0 0 1rem', color: '#856404' }}>
              📝 Submit New Ticket
            </h4>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                Issue Category *
              </label>
              <select
                value={newTicket.issue_category}
                onChange={(e) => setNewTicket({ ...newTicket, issue_category: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
              >
                <option value="">Select a category...</option>
                {issueCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                Describe Your Issue *
              </label>
              <textarea
                value={newTicket.issue}
                onChange={(e) => setNewTicket({ ...newTicket, issue: e.target.value })}
                rows={4}
                placeholder="Please describe your issue in detail..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>
            
            {/* File Attachment */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                📎 Attach File (Optional)
              </label>
              <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                You can attach a screenshot, document, or any file that helps explain your issue (max 10MB)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  backgroundColor: 'white'
                }}
              />
              {selectedFile && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem',
                  backgroundColor: '#d4edda',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ fontSize: '0.85rem', color: '#155724' }}>
                    📄 {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </span>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#dc3545',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      padding: '0 0.5rem'
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>

            <div style={{
              backgroundColor: '#e7f3ff',
              padding: '0.75rem',
              borderRadius: '6px',
              marginBottom: '1rem',
              fontSize: '0.85rem',
              color: '#0066cc'
            }}>
              <strong>Submitting as:</strong> {currentUser?.name || currentUser?.username || 'Unknown User'} ({currentUser?.username || 'N/A'})
            </div>
            
            <button
              onClick={handleSubmitTicket}
              disabled={submitting}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: submitting ? '#6c757d' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: '500'
              }}
            >
              {submitting ? '⏳ Submitting...' : '✓ Submit Ticket'}
            </button>
          </div>
        )}

        {/* Confirmation Message (shown after successful submission) */}
        {showConfirmation && (
          <div style={{
            backgroundColor: '#d4edda',
            border: '2px solid #28a745',
            borderRadius: '12px',
            padding: '2rem',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
            <h3 style={{ color: '#155724', margin: '0 0 1rem' }}>
              Ticket Submitted Successfully!
            </h3>
            <p style={{ 
              color: '#155724', 
              fontSize: '1rem', 
              lineHeight: '1.6',
              maxWidth: '600px',
              margin: '0 auto 1.5rem'
            }}>
              {confirmationMessage}
            </p>
            <button
              onClick={() => {
                setShowConfirmation(false);
                setShowNewTicketForm(true);
              }}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500',
                marginRight: '1rem'
              }}
            >
              ➕ Submit Another Ticket
            </button>
            <button
              onClick={() => setShowConfirmation(false)}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500'
              }}
            >
              ✓ Done
            </button>
          </div>
        )}

        {/* Tickets Table - Visible to all users (admins see all, users see their own) */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #007bff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ marginTop: '1rem', color: '#666' }}>Loading tickets...</p>
          </div>
        ) : filteredTickets.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎫</div>
            <p style={{ fontSize: '1.1rem', color: '#666', margin: 0 }}>
              {tickets.length === 0
                ? (isTicketAdmin ? 'No tickets yet' : 'You haven\'t submitted any tickets yet')
                : statusFilter === 'open'
                  ? 'No open tickets'
                  : statusFilter === 'closed'
                    ? 'No closed tickets'
                    : (isTicketAdmin ? 'No tickets yet' : 'You haven\'t submitted any tickets yet')}
            </p>
            <p style={{ color: '#999', marginTop: '0.5rem' }}>
              {tickets.length === 0
                ? (isTicketAdmin
                  ? 'Tickets submitted by users will appear here'
                  : 'Click "New Ticket" above to submit a bug report, feature request, or any feedback')
                : 'Switch to All or another status filter to see the rest of your tickets'}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            {isTicketAdmin && (
              <div style={{
                backgroundColor: '#fff3cd',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                marginBottom: '1rem',
                fontSize: '0.85rem',
                color: '#856404'
              }}>
                🔒 <strong>Admin View:</strong> You can see all tickets from all users and respond to them
              </div>
            )}
            {!isTicketAdmin && (
              <div style={{
                backgroundColor: '#e7f3ff',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                marginBottom: '1rem',
                fontSize: '0.85rem',
                color: '#004085'
              }}>
                📋 <strong>Your Tickets:</strong> Below are the tickets you have submitted
              </div>
            )}
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.9rem',
              tableLayout: 'fixed'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#343a40', color: 'white' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', width: '70px' }}>Ticket #</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', width: '130px' }}>Submitted</th>
                  {isTicketAdmin && <th style={{ padding: '0.75rem', textAlign: 'left', width: '140px' }}>User</th>}
                  <th style={{ padding: '0.75rem', textAlign: 'left', width: '110px' }}>Category</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Issue</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', width: '80px' }}>Attachment</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Response</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', width: '90px' }}>Status</th>
                  {isTicketAdmin && <th style={{ padding: '0.75rem', textAlign: 'center', width: '100px' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredTickets.map((ticket, index) => (
                  <React.Fragment key={ticket.id}>
                    <tr 
                      style={{
                        backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa',
                        borderBottom: expandedTicketId === ticket.id ? 'none' : '1px solid #dee2e6',
                        cursor: 'pointer'
                      }}
                      onClick={() => { const newId = expandedTicketId === ticket.id ? null : ticket.id; setExpandedTicketId(newId); if (newId) fetchReplies(newId); }}
                    >
                      <td style={{ padding: '0.75rem', fontWeight: '600', color: '#007bff' }}>
                        #{ticket.id}
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                          {expandedTicketId === ticket.id ? '▼' : '▶'}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', color: '#666', fontSize: '0.85rem' }}>
                        {ticket.created_at ? new Date(ticket.created_at).toLocaleString() : '-'}
                      </td>
                      {isTicketAdmin && (
                        <td style={{ padding: '0.75rem' }}>
                          <div style={{ fontWeight: '500' }}>{ticket.user_name || 'Unknown'}</div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>{ticket.user_email}</div>
                        </td>
                      )}
                      <td style={{ padding: '0.75rem' }}>
                        <span style={{
                          backgroundColor: '#e9ecef',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.85rem'
                        }}>
                          {ticket.issue_category}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', overflow: 'hidden' }}>
                        <div style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }} title={ticket.issue}>
                          {ticket.issue}
                        </div>
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        {ticket.attachment_name ? (
                          <button
                            onClick={() => handleDownloadAttachment(ticket.id, ticket.attachment_name)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#007bff',
                              color: 'white',
                              borderRadius: '4px',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                            title={ticket.attachment_name}
                          >
                            📎 View
                          </button>
                        ) : (
                          <span style={{ color: '#999', fontSize: '0.8rem' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', overflow: 'hidden' }}>
                        {ticket.response ? (
                          <div style={{
                            backgroundColor: '#d4edda',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.85rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }} title={ticket.response}>
                            {ticket.response}
                          </div>
                        ) : (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>
                            Awaiting response...
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <StatusBadge status={ticket.status} />
                      </td>
                      {isTicketAdmin && (
                        <td style={{ padding: '0.75rem', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleDeleteTicket(ticket.id)}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                            title="Delete ticket"
                          >
                            🗑️
                          </button>
                        </td>
                      )}
                    </tr>
                    
                    {/* Expanded row: conversation thread */}
                    {expandedTicketId === ticket.id && (
                      <tr style={{ backgroundColor: '#f0f4f8' }}>
                        <td colSpan={isTicketAdmin ? 9 : 7} style={{ padding: '1rem', borderBottom: '1px solid #dee2e6' }}>
                          {/* Original issue */}
                          <div style={{ marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                              <strong style={{ color: '#333' }}>📝 {ticket.user_name || ticket.user_email}</strong>
                              <span style={{ fontSize: '0.8rem', color: '#999' }}>{ticket.created_at ? new Date(ticket.created_at).toLocaleString() : ''}</span>
                              <span style={{ fontSize: '0.75rem', backgroundColor: '#e9ecef', padding: '0.15rem 0.5rem', borderRadius: 4 }}>{ticket.issue_category}</span>
                            </div>
                            <div style={{ backgroundColor: 'white', padding: '0.75rem 1rem', borderRadius: 6, border: '1px solid #ddd', whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                              {ticket.issue}
                            </div>
                          </div>

                          {/* Replies thread */}
                          {(ticketReplies[ticket.id] || []).map(reply => (
                            <div key={reply.id} style={{ marginBottom: '0.75rem', marginLeft: reply.is_admin ? 0 : '2rem', marginRight: reply.is_admin ? '2rem' : 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                <strong style={{ color: reply.is_admin ? '#17a2b8' : '#333', fontSize: '0.85rem' }}>
                                  {reply.is_admin ? '🛡️' : '👤'} {reply.reply_by_name || reply.reply_by_email}
                                </strong>
                                <span style={{ fontSize: '0.75rem', color: '#999' }}>{new Date(reply.created_at).toLocaleString()}</span>
                                {reply.is_admin && <span style={{ fontSize: '0.7rem', backgroundColor: '#17a2b8', color: 'white', padding: '0.1rem 0.4rem', borderRadius: 3 }}>Admin</span>}
                              </div>
                              <div style={{
                                backgroundColor: reply.is_admin ? '#d1ecf1' : '#fff',
                                padding: '0.75rem 1rem',
                                borderRadius: 6,
                                border: `1px solid ${reply.is_admin ? '#17a2b8' : '#ddd'}`,
                                whiteSpace: 'pre-wrap',
                                fontSize: '0.9rem'
                              }}>
                                {reply.message}
                              </div>
                            </div>
                          ))}

                          {/* Reply form (visible to everyone if ticket is not closed) */}
                          {ticket.status !== 'Closed' ? (
                            <div style={{ marginTop: '1rem', backgroundColor: 'white', padding: '1rem', borderRadius: 8, border: '1px solid #dee2e6' }}>
                              <textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                rows={3}
                                placeholder={isTicketAdmin ? 'Reply to this ticket...' : 'Add a reply...'}
                                style={{ width: '100%', padding: '0.6rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }}
                              />
                              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', alignItems: 'center' }}>
                                <button
                                  onClick={() => handleSendReply(ticket.id)}
                                  disabled={replyLoading || !replyText.trim()}
                                  style={{ padding: '0.4rem 1rem', backgroundColor: replyLoading ? '#6c757d' : '#28a745', color: 'white', border: 'none', borderRadius: 6, cursor: replyLoading ? 'not-allowed' : 'pointer', fontSize: '0.85rem' }}
                                >
                                  {replyLoading ? '⏳ Sending...' : '✉️ Send Reply'}
                                </button>
                                {isTicketAdmin && (
                                  <button
                                    onClick={() => handleCloseTicket(ticket.id)}
                                    style={{ padding: '0.4rem 1rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem' }}
                                  >
                                    Close Ticket
                                  </button>
                                )}
                                <span style={{ fontSize: '0.8rem', color: '#999', marginLeft: 'auto' }}>📧 Email notification will be sent</span>
                              </div>
                            </div>
                          ) : (
                            <div style={{ marginTop: '1rem', textAlign: 'center', padding: '0.75rem', backgroundColor: '#f8d7da', borderRadius: 6, color: '#721c24', fontSize: '0.9rem' }}>
                              This ticket is closed.
                              {isTicketAdmin && (
                                <button onClick={() => handleReopenTicket(ticket.id)} style={{ marginLeft: '1rem', padding: '0.3rem 0.8rem', backgroundColor: '#ffc107', color: '#333', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.85rem' }}>
                                  Reopen
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};

export default ContactUs;

